import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import AppShell from "@/components/AppShell";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  LogOut, Mail, User as UserIcon, Eye, EyeOff, Building2, Globe, Download, Trash2,
  Shield, Lock, ChevronRight, Loader2, ShieldCheck,
} from "lucide-react";
import { toast } from "sonner";

const VISIBILITY_OPTIONS = [
  { id: "public", label: "Public", desc: "Votre profil est visible par tous les visiteurs.", Icon: Globe },
  { id: "brands_only", label: "Créateurs connectés uniquement", desc: "Seuls les créateurs connectés peuvent voir votre profil.", Icon: Building2 },
  { id: "private", label: "Privé", desc: "Votre profil n'apparaît plus dans les recherches publiques.", Icon: Eye },
];

export default function Settings() {
  const { user, loading, logout, checkAuth } = useAuth();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState({ first_name: "", last_name: "" });
  const [savingInfo, setSavingInfo] = useState(false);
  const [savingVis, setSavingVis] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [deletePwd, setDeletePwd] = useState("");
  const [deleteConfirm, setDeleteConfirm] = useState("");
  const [deleteSubmitting, setDeleteSubmitting] = useState(false);

  useEffect(() => {
    if (!loading && !user) navigate("/login");
    if (user) {
      setForm({
        first_name: user.first_name || (user.name?.split(" ")[0] ?? ""),
        last_name: user.last_name || (user.name?.split(" ").slice(1).join(" ") ?? ""),
      });
    }
  }, [user, loading, navigate]);

  if (!user) return null;

  const isEmailAuth = user.auth_provider === "email";
  const visibility = user.account_visibility || "public";

  const saveInfo = async () => {
    setSavingInfo(true);
    try {
      await api.put("/privacy/me", form);
      await checkAuth();
      toast.success("Informations mises à jour");
      setEditing(false);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally {
      setSavingInfo(false);
    }
  };

  const setVisibility = async (id) => {
    if (id === visibility) return;
    setSavingVis(true);
    try {
      await api.put("/privacy/visibility", { visibility: id });
      await checkAuth();
      toast.success("Visibilité mise à jour");
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
    } finally {
      setSavingVis(false);
    }
  };

  const exportData = async () => {
    setExportLoading(true);
    try {
      const { data } = await api.get("/privacy/export");
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: "application/json" });
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `helencia-export-${user.user_id}.json`;
      a.click();
      URL.revokeObjectURL(a.href);
      toast.success("Vos données ont été téléchargées");
    } catch {
      toast.error("Erreur lors de l'export");
    } finally {
      setExportLoading(false);
    }
  };

  const submitDelete = async () => {
    if (deleteConfirm.trim().toUpperCase() !== "SUPPRIMER") {
      toast.error("Veuillez taper SUPPRIMER pour confirmer");
      return;
    }
    setDeleteSubmitting(true);
    try {
      const body = { confirm_text: deleteConfirm };
      if (isEmailAuth) body.password = deletePwd;
      await api.post("/privacy/delete", body);
      toast.success("Votre compte a été supprimé.");
      localStorage.removeItem("helencia_token");
      setTimeout(() => { window.location.href = "/"; }, 800);
    } catch (e) {
      toast.error(typeof e.response?.data?.detail === "string" ? e.response.data.detail : "Erreur");
      setDeleteSubmitting(false);
    }
  };

  return (
    <AppShell>
      <div className="mb-8">
        <p className="caption-label text-[#C68E59]">Paramètres</p>
        <h1 className="font-serif text-4xl sm:text-5xl text-[#1A1A1A] mt-1">Compte &amp; confidentialité</h1>
        <p className="text-sm text-[#8B8680] mt-2">Gérez votre identité, vos préférences et vos droits RGPD.</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* === Mes informations === */}
        <section className="lg:col-span-2 bg-white rounded-3xl border border-[#EADDC9] p-8" data-testid="settings-info">
          <div className="flex items-center justify-between gap-4 pb-5 border-b border-[#FAF3EA]">
            <div className="flex items-center gap-4 min-w-0">
              <div className="w-14 h-14 rounded-full bg-[#F5E6D0] flex items-center justify-center flex-shrink-0">
                <UserIcon className="w-6 h-6 text-[#1A1A1A]" />
              </div>
              <div className="min-w-0">
                <p className="font-serif text-2xl text-[#1A1A1A] truncate">{user.name}</p>
                <p className="text-sm text-[#8B8680] truncate">{user.email}</p>
              </div>
            </div>
            {!editing && (
              <Button variant="outline" onClick={() => setEditing(true)} className="rounded-full border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#FAF3EA]" data-testid="settings-edit-btn">
                Modifier
              </Button>
            )}
          </div>

          {editing ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 mt-5" data-testid="settings-edit-form">
              <div>
                <Label className="caption-label">Prénom</Label>
                <Input value={form.first_name} onChange={(e) => setForm({ ...form, first_name: e.target.value })} className="rounded-xl mt-2 h-11" data-testid="settings-firstname" />
              </div>
              <div>
                <Label className="caption-label">Nom</Label>
                <Input value={form.last_name} onChange={(e) => setForm({ ...form, last_name: e.target.value })} className="rounded-xl mt-2 h-11" data-testid="settings-lastname" />
              </div>
              <div className="sm:col-span-2 flex gap-2 mt-2">
                <Button onClick={saveInfo} disabled={savingInfo} className="rounded-full bg-[#C68E59] hover:bg-[#A26F3E] text-white" data-testid="settings-save-info">
                  {savingInfo ? <Loader2 className="w-4 h-4 animate-spin" /> : "Enregistrer"}
                </Button>
                <Button variant="outline" onClick={() => setEditing(false)} className="rounded-full">Annuler</Button>
              </div>
            </div>
          ) : (
            <div className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-5 text-sm">
              <Field label="Connexion" value={isEmailAuth ? "Email & mot de passe" : "Google"} icon={isEmailAuth ? Lock : Mail} />
              <Field label="Rôle" value={{ talent: "Talent", professional: "Créateur", admin: "Admin" }[user.role] || user.role} icon={ShieldCheck} />
              {user.birth_date && <Field label="Date de naissance" value={new Date(user.birth_date).toLocaleDateString("fr-FR")} icon={UserIcon} />}
              <Field label="Email vérifié" value={user.email_verified ? "Oui" : "Non"} icon={Shield} />
            </div>
          )}

          <Button
            variant="outline"
            onClick={logout}
            className="mt-8 rounded-full border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#FAF3EA]"
            data-testid="logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Se déconnecter
          </Button>
        </section>

        {/* === Visibilité === */}
        <section className="bg-white rounded-3xl border border-[#EADDC9] p-8" data-testid="settings-visibility">
          <p className="caption-label text-[#A26F3E]">Visibilité du profil</p>
          <h2 className="font-serif text-2xl text-[#1A1A1A] mt-1">Qui peut me voir ?</h2>
          <div className="space-y-2 mt-5">
            {VISIBILITY_OPTIONS.map((o) => {
              const active = visibility === o.id;
              const Icon = o.Icon;
              return (
                <button
                  key={o.id}
                  type="button"
                  disabled={savingVis}
                  onClick={() => setVisibility(o.id)}
                  className={`w-full text-left p-4 rounded-2xl border-2 transition flex items-start gap-3 ${active ? "border-[#C68E59] bg-[#FAF3EA]" : "border-[#EADDC9] bg-white hover:border-[#D4A574]"}`}
                  data-testid={`settings-visibility-${o.id}`}
                >
                  <Icon className={`w-4 h-4 mt-1 flex-shrink-0 ${active ? "text-[#C68E59]" : "text-[#A26F3E]"}`} />
                  <div className="min-w-0">
                    <p className="text-sm font-semibold text-[#1A1A1A]">{o.label}</p>
                    <p className="text-xs text-[#8C7D6B] mt-0.5">{o.desc}</p>
                  </div>
                  {active && <ChevronRight className="w-4 h-4 text-[#C68E59] ml-auto flex-shrink-0 mt-1" />}
                </button>
              );
            })}
          </div>
        </section>

        {/* === RGPD === */}
        <section className="lg:col-span-3 bg-white rounded-3xl border border-[#EADDC9] p-8" data-testid="settings-rgpd">
          <div className="flex items-center gap-3 mb-1">
            <Shield className="w-5 h-5 text-[#C68E59]" />
            <p className="caption-label text-[#A26F3E]">Mes droits RGPD</p>
          </div>
          <h2 className="font-serif text-2xl text-[#1A1A1A] mt-1">Vos données vous appartiennent.</h2>
          <p className="text-sm text-[#6E4B2A] mt-2 max-w-2xl">
            Conformément au RGPD, vous disposez d'un droit d'accès, de portabilité et de suppression. Vous pouvez à tout moment télécharger l'ensemble de vos données ou supprimer votre compte définitivement.
          </p>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mt-6">
            <div className="rounded-2xl border border-[#EADDC9] p-5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-[#1A1A1A] flex items-center gap-2">
                  <Download className="w-4 h-4 text-[#C68E59]" /> Télécharger mes données
                </p>
                <p className="text-xs text-[#8C7D6B] mt-1">Format JSON portable. Profil, portfolio, candidatures, notifications.</p>
              </div>
              <Button onClick={exportData} disabled={exportLoading} variant="outline" className="rounded-full border-[#1A1A1A]" data-testid="settings-export-btn">
                {exportLoading ? <Loader2 className="w-4 h-4 animate-spin" /> : "Exporter"}
              </Button>
            </div>

            <div className="rounded-2xl border border-[#F2C0B7] bg-[#FEEFEC] p-5 flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="font-semibold text-[#A3483F] flex items-center gap-2">
                  <Trash2 className="w-4 h-4" /> Supprimer mon compte
                </p>
                <p className="text-xs text-[#A3483F]/80 mt-1">Action définitive. Vos données seront anonymisées sous 30 jours.</p>
              </div>
              <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
                <DialogTrigger asChild>
                  <Button variant="outline" className="rounded-full border-[#A3483F] text-[#A3483F] hover:bg-[#FEEFEC]" data-testid="settings-delete-btn">
                    Supprimer
                  </Button>
                </DialogTrigger>
                <DialogContent className="rounded-3xl">
                  <DialogHeader>
                    <DialogTitle className="font-serif text-2xl">Supprimer définitivement votre compte ?</DialogTitle>
                    <DialogDescription className="text-[#6E4B2A]">
                      Cette action est irréversible. Votre profil, votre portfolio et vos candidatures seront anonymisés. Tapez <strong>SUPPRIMER</strong> pour confirmer.
                    </DialogDescription>
                  </DialogHeader>
                  <div className="space-y-3 mt-2">
                    {isEmailAuth && (
                      <div>
                        <Label className="caption-label">Mot de passe actuel</Label>
                        <Input type="password" value={deletePwd} onChange={(e) => setDeletePwd(e.target.value)} className="rounded-xl mt-2 h-11" data-testid="delete-password" />
                      </div>
                    )}
                    <div>
                      <Label className="caption-label">Confirmation</Label>
                      <Input value={deleteConfirm} onChange={(e) => setDeleteConfirm(e.target.value)} placeholder='Tapez SUPPRIMER' className="rounded-xl mt-2 h-11" data-testid="delete-confirm" />
                    </div>
                  </div>
                  <DialogFooter>
                    <Button variant="outline" onClick={() => setDeleteOpen(false)} className="rounded-full">Annuler</Button>
                    <Button onClick={submitDelete} disabled={deleteSubmitting} className="rounded-full bg-[#A3483F] hover:bg-[#7A2F27] text-white" data-testid="delete-submit">
                      {deleteSubmitting ? <Loader2 className="w-4 h-4 animate-spin" /> : "Supprimer définitivement"}
                    </Button>
                  </DialogFooter>
                </DialogContent>
              </Dialog>
            </div>
          </div>
        </section>
      </div>
    </AppShell>
  );
}

function Field({ label, value, icon: Icon }) {
  return (
    <div>
      <p className="caption-label">{label}</p>
      <p className="mt-1 text-sm text-[#1A1A1A] flex items-center gap-2">
        {Icon && <Icon className="w-4 h-4 text-[#A26F3E]" />}{value}
      </p>
    </div>
  );
}
